/*
 * Copyright (c) 2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
/// This AST is mostly compatible with [mdast](https://github.com/syntax-tree/mdast/)
/* eslint-disable @typescript-eslint/interface-name-prefix */
import Object from 'apmui/util/Object';

export const enum NODE_TYPE {
    BLOCK = 'block',
    BREAK = 'break',
    EMPHASIS = 'emphasis',
    HEADING = 'heading',
    IMAGE = 'image',
    LINK = 'link',
    LIST = 'list',
    LIST_ITEM = 'listItem',
    PARAGRAPH = 'paragraph',
    ROOT = 'root',
    SPAN = 'span',
    STRONG = 'strong',
    TEXT = 'text',
    THEMATIC_BREAK = 'thematicBreak',
}

export interface NodeMap {
    [NODE_TYPE.BLOCK]: Block;
    [NODE_TYPE.BREAK]: Break;
    [NODE_TYPE.EMPHASIS]: Emphasis;
    [NODE_TYPE.HEADING]: Heading;
    [NODE_TYPE.IMAGE]: Image;
    [NODE_TYPE.LINK]: Link;
    [NODE_TYPE.LIST]: List;
    [NODE_TYPE.LIST_ITEM]: ListItem;
    [NODE_TYPE.PARAGRAPH]: Paragraph;
    [NODE_TYPE.ROOT]: Root;
    [NODE_TYPE.SPAN]: Span;
    [NODE_TYPE.STRONG]: Strong;
    [NODE_TYPE.TEXT]: Text;
    [NODE_TYPE.THEMATIC_BREAK]: ThematicBreak;
}

type Omit<K, T> = Pick<T, Exclude<keyof T, K>>;
type NodeArgs<N> = Partial<Omit<'type', N>>;

export class BaseNode {
    type?: NODE_TYPE;
    className?: string;
    id?: string;
}

export abstract class Parent extends BaseNode {
    children!: Content[];
}

export class Root extends Parent {
    type: NODE_TYPE.ROOT = NODE_TYPE.ROOT;
    children: Content[] = [];

    constructor(args?: NodeArgs<Root>) {
        super();
        Object.assign(this, args);
    }
}

export class Block extends Parent {
    type: NODE_TYPE.BLOCK = NODE_TYPE.BLOCK;
    children: Content[] = [];

    constructor(args?: NodeArgs<Block>) {
        super();
        Object.assign(this, args);
    }
}

export class Span extends Parent {
    type: NODE_TYPE.SPAN = NODE_TYPE.SPAN;
    children: PhrasingContent[] = [];

    constructor(args?: NodeArgs<Span>) {
        super();
        Object.assign(this, args);
    }
}

export class Paragraph extends Parent {
    type: NODE_TYPE.PARAGRAPH = NODE_TYPE.PARAGRAPH;
    children: PhrasingContent[] = [];

    constructor(args?: NodeArgs<Paragraph>) {
        super();
        Object.assign(this, args);
    }
}

export class Heading extends Parent {
    type: NODE_TYPE.HEADING = NODE_TYPE.HEADING;
    depth: 1 | 2 | 3 | 4 | 5 | 6 = 1;
    children: PhrasingContent[] = [];

    constructor(args?: NodeArgs<Heading>) {
        super();
        Object.assign(this, args);
    }
}

export class ThematicBreak extends BaseNode {
    type: NODE_TYPE.THEMATIC_BREAK = NODE_TYPE.THEMATIC_BREAK;

    constructor(args?: NodeArgs<ThematicBreak>) {
        super();
        Object.assign(this, args);
    }
}

export class List extends Parent {
    type: NODE_TYPE.LIST = NODE_TYPE.LIST;
    ordered?: boolean;
    start?: number;
    children: List[] = [];

    constructor(args?: NodeArgs<List>) {
        super();
        Object.assign(this, args);
    }
}

export class ListItem extends Parent {
    type: NODE_TYPE.LIST_ITEM = NODE_TYPE.LIST_ITEM;
    children: BlockContent[] = [];

    constructor(args?: NodeArgs<ListItem>) {
        super();
        Object.assign(this, args);
    }
}

export class Text extends BaseNode {
    type: NODE_TYPE.TEXT = NODE_TYPE.TEXT;
    value: string = '';

    constructor(args?: NodeArgs<Text>) {
        super();
        Object.assign(this, args);
    }
}

export class Emphasis extends Parent {
    type: NODE_TYPE.EMPHASIS = NODE_TYPE.EMPHASIS;
    children: PhrasingContent[] = [];

    constructor(args?: NodeArgs<Emphasis>) {
        super();
        Object.assign(this, args);
    }
}

export class Strong extends Parent {
    type: NODE_TYPE.STRONG = NODE_TYPE.STRONG;
    children: PhrasingContent[] = [];

    constructor(args?: NodeArgs<Strong>) {
        super();
        Object.assign(this, args);
    }
}

export class Break extends BaseNode {
    type: NODE_TYPE.BREAK = NODE_TYPE.BREAK;

    constructor(args?: NodeArgs<Link>) {
        super();
        Object.assign(this, args);
    }
}

export class Link extends Parent {
    type: NODE_TYPE.LINK = NODE_TYPE.LINK;
    url: string = '';
    target?: string;
    title?: string;
    children: StaticPhrasingContent[] = [];

    constructor(args?: NodeArgs<Link>) {
        super();
        Object.assign(this, args);
    }
}

export class Image extends BaseNode {
    type: NODE_TYPE.IMAGE = NODE_TYPE.IMAGE;
    url: string = '';
    title?: string;
    alt?: string;

    constructor(args?: NodeArgs<Image>) {
        super();
        Object.assign(this, args);
    }
}

export type Content = TopLevelContent | PhrasingContent;

export type TopLevelContent = BlockContent;

export type BlockContent = Paragraph | Heading | ThematicBreak | List | Block;

export type ListContent = ListItem;

export type PhrasingContent = StaticPhrasingContent | Link;

export type StaticPhrasingContent = Text | Emphasis | Strong | Break | Image | Span;

/* Union type for any Syntax Tree node */
export type Node = Root | Content;

export function create<T extends NODE_TYPE>(type: T, args?: NodeArgs<NodeMap[T]>): NodeMap[T] {
    switch (type as NODE_TYPE) {
        case NODE_TYPE.BLOCK:
            return new Block(args);
        case NODE_TYPE.BREAK:
            return new Break(args);
        case NODE_TYPE.EMPHASIS:
            return new Emphasis(args);
        case NODE_TYPE.HEADING:
            return new Heading(args);
        case NODE_TYPE.IMAGE:
            return new Image(args);
        case NODE_TYPE.LINK:
            return new Link(args);
        case NODE_TYPE.LIST:
            return new List(args);
        case NODE_TYPE.LIST_ITEM:
            return new ListItem(args);
        case NODE_TYPE.PARAGRAPH:
            return new Paragraph(args);
        case NODE_TYPE.ROOT:
            return new Root(args);
        case NODE_TYPE.STRONG:
            return new Strong(args);
        case NODE_TYPE.SPAN:
            return new Span(args);
        case NODE_TYPE.TEXT:
            return new Text(args);
        case NODE_TYPE.THEMATIC_BREAK:
            return new ThematicBreak(args);
    }
}
